import { ChangeEvent, useState } from 'react';
import {
  Chips,
  FilterMenu,
  FilterMenuContent,
  FilterMenuItem,
  FlexContainer,
  Input,
} from '@facephi/ui-react';
import { useTranslation } from 'react-i18next';
import { ALERT_TYPE, SOURCE_CONFIG } from '../../state';
import { FILTERS, ListOperationHeaderProps } from './model';

const FILTERS_CONFIG = {
  source: SOURCE_CONFIG,
  alert_type: ALERT_TYPE,
};

const CHIPS_CONFIG: { [key in string]: (value: string) => string } = {
  source: (value: string) => SOURCE_CONFIG[value as keyof typeof SOURCE_CONFIG],
  alert_type: (value: string) => ALERT_TYPE[value as keyof typeof ALERT_TYPE],
};

export const ListOperationHeader = ({
  onChangeFilter,
}: ListOperationHeaderProps) => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<FILTERS>({});
  const removeFilter = (type: string) => {
    return Object.entries(filters).reduce(
      (acc, [key, value]) =>
        key === type
          ? acc
          : {
              ...acc,
              [key]: value,
            },
      {}
    );
  };

  const handleSelector = (type: string, value: string) => {
    let innerFilter: FILTERS;
    if (isFilterSelected(type, value)) {
      innerFilter = removeFilter(type);
    } else {
      innerFilter = {
        ...filters,
        [type]: value,
      };
    }
    handleFilters(innerFilter);
  };

  const handleScore = (value?: ChangeEvent<HTMLInputElement> | undefined) => {
    const newValue = value?.target.value;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, prefer-const
    let { score, ...innerFilter } = filters;
    if (newValue) {
      innerFilter = {
        ...innerFilter,
        score: Number(newValue),
      } as FILTERS;
    }
    handleFilters(innerFilter);
  };

  const handleFilters = (innerFilter: FILTERS) => {
    setFilters(innerFilter);
    onChangeFilter?.(innerFilter);
  };

  const isFilterSelected = (type: string, value: string) => {
    return filters[type as keyof FILTERS] === value;
  };

  const handleChip = (type: string) => {
    handleFilters(removeFilter(type));
  };

  return (
    <FlexContainer columnGap="2.4" alignItems="flex-start">
      <Input
        type="number"
        placeholder={t('Risk') as string}
        onChange={handleScore}
        min={0}
        max={100}
      />
      <FilterMenu>
        {Object.entries(FILTERS_CONFIG).map(([type, items]) => (
          <FilterMenuContent title={t(type) as string} key={type}>
            {Object.entries(items).map(([value, title]) => (
              <FilterMenuItem
                key={`${type}-${value}`}
                testId={value}
                checked={isFilterSelected(type, value)}
                onChange={() => handleSelector(type, value)}
              >
                {t(title)}
              </FilterMenuItem>
            ))}
          </FilterMenuContent>
        ))}
      </FilterMenu>
      <FlexContainer columnGap="1.2" rowGap="1.2" wrap="wrap">
        {Object.entries(filters).map(
          ([item, value]) =>
            Object.keys(FILTERS_CONFIG).includes(item) && (
              <Chips
                key={`${item}-${value}`}
                label={t(CHIPS_CONFIG[item](value as string))}
                onClick={() => handleChip(item)}
                closeable
              />
            )
        )}
      </FlexContainer>
    </FlexContainer>
  );
};
